<template>
<header>
  <h1>Header One</h1>
</header>
</template>

<script lang="ts">
import { defineNuxtComponent, navigateTo } from '#app';
import { mapState } from 'pinia';
import { MenuTypes, ViewTypes } from '~/constants/ui.constants';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';
import { useCoreStore } from '~/units/core/store';

interface MenuItem {
  name: string;
  url: string;
  has_sub_menu: boolean;
  is_open_new_tab: boolean;
  sub_menus?: MenuItem[];
}

interface SocialItem {
  url: string;
  iconClass: string;
}

const getCssColorVar = (colorKeyOrValue: string | undefined | null): string => {
  if (!colorKeyOrValue) return '';
  if (colorKeyOrValue.startsWith('#') || colorKeyOrValue.startsWith('rgb') || colorKeyOrValue.startsWith('hsl'))
    return colorKeyOrValue;
  const cleanedKey = colorKeyOrValue.replace(/-/g, '').toLowerCase();
  return `var(--c-${cleanedKey})`;
};

export default defineNuxtComponent({
  name: 'Theme9BaseHeaderOne',
  mixins: [ComponentMixin],
  data() {
    return {
      menuVisible: false,
      isScrolled: false,
      openSubMenu: null as number | null,
      isPopupOpen: false, // New data property
      variants: { v1: 'v1', HEADER_2: 'HEADER_2' },
      scrollThreshold: 50,
    };
  },
  computed: {
    menu() {
      return this.component?.variables['menu'] || {};
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
    ...mapState(useWebsiteStore, ['headerMenuItems', 'company']),
    ...mapState(useCoreStore, ['isAnythingOnTopVisible']),
    homeLink(): string {
      return '/';
    },
    logo(): string {
      const currentCompany = this.company as any;
      const defaultLogo = currentCompany?.logo || '';
      // Show the light logo only if the menu is open.
      if (this.menuVisible) {
        return currentCompany?.light_logo || defaultLogo;
      }
      return defaultLogo;
    },
    columnOneMenu(): MenuItem[] {
      const content = this.component?.variables['column_one_content-text'];
      if (content === MenuTypes.HEADER_MENU) {
        return (this.headerMenuItems as MenuItem[]) || [];
      }
      return [];
    },
    headerStyle(): Record<string, string> {
      const styles: Record<string, string> = {};
      styles.backgroundColor = this.isScrolled ? getCssColorVar(this.vars?.backgroundColor as string) : 'transparent';
      const textColor = getCssColorVar(this.vars?.textColor as string);
      if (textColor) styles.color = textColor;
      styles.transition = 'background-color 0.3s ease, color 0.3s ease';
      if (this.menuVisible) {
        styles.backgroundColor = getCssColorVar(this.vars?.backgroundColor as string);
      }
      return styles;
    },
    menuStyle(): Record<string, string> {
      const styles: Record<string, string> = {};
      if (this.menuVisible) {
        styles.backgroundColor = '#ffffff';
      } else {
        const bgColor = getCssColorVar(this.vars?.backgroundColor as string);
        if (bgColor) styles.backgroundColor = bgColor;
      }
      const textColor = getCssColorVar(this.vars?.textColor as string);
      if (textColor) styles.color = textColor;
      return styles;
    },
  },
  methods: {
    toggleMenu(): void {
      this.menuVisible = !this.menuVisible;
      if (this.menuVisible) {
        document.body.classList.add('no-scroll');
        this.openSubMenu = null;
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
    toggleSubMenu(index: number): void {
      this.openSubMenu = this.openSubMenu === index ? null : index;
    },
    handleMenuLinkClick(menuItem: MenuItem): void {
      if (!menuItem || menuItem.has_sub_menu || !menuItem.url) return;
      navigateTo(menuItem.url, {
        external: menuItem.is_open_new_tab || false,
        open: { target: menuItem.is_open_new_tab ? '_blank' : '_self' },
      });
      this.toggleMenu();
    },
    changeLanguage(lang: string): void {},
    handleScroll(): void {
      this.isScrolled = window.scrollY > this.scrollThreshold;
    },
    checkPopupOpen(): void {
      this.isPopupOpen = document.body.classList.contains('popup-open');
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    this.checkPopupOpen();
    const observer = new MutationObserver(() => this.checkPopupOpen());
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.$data.popupObserver = observer; // Store observer for cleanup
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.body.classList.remove('no-scroll');
    this.$data.popupObserver?.disconnect(); // Cleanup observer
  },
});
</script>

